import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const offerPricingArticle: BlogArticle = {
  key: BlogArticleKey.OFFER_PRICING,
  slug: BlogSlugs.OFFER_PRICING,
  categoryId: "02",
  date: "2026-08-30",
  readTime: 7,
  locales: {
    fr: {
      title: "Fixer son prix à partir de ce que le problème coûte",
      excerpt:
        "Regarder la concurrence donne un chiffre plausible et jamais le bon. Le seul repère utile est ce que le problème coûte à celui qui le vit, et il se trouve dans tes appels.",
      description:
        "Comment fixer le prix d'une offre à partir du coût réel du problème, où trouver ce chiffre, et comment présenter le prix pour qu'il ne soit pas le premier sujet.",
      ctaTitle: "Trouve ton prix dans tes appels",
      ctaText:
        "On repère ce que tes clients disent perdre, en temps et en argent, avant d'acheter. C'est ce chiffre qui rend ton prix évident. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La méthode la plus répandue pour fixer un prix consiste à regarder ce que font les autres, puis à se placer un peu en dessous. Elle a deux avantages : elle est rapide, et elle ne demande de parler à personne.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Elle a un défaut qui annule les deux. Un concurrent vend autre chose, à quelqu'un d'autre, avec une structure de coûts que tu ne connais pas. Son prix ne dit rien de ce que ton offre vaut, il dit seulement ce que lui a décidé.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Un prix ne se déduit pas de ce que ça te coûte à produire, ni de ce que les autres demandent. Il se déduit de ce que le problème coûte à celui qui le vit.",
        },
        { type: ArticleBlockType.H2, text: "Le seul chiffre qui sert de repère" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Avant d'annoncer un prix, il faut pouvoir répondre à une question : combien perd la personne, chaque mois, à cause du problème que tu règles. Tant que ce chiffre n'existe pas, aucun prix n'est justifiable, ni le tien ni un autre.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce chiffre a trois formes, et la plupart des offres n'en exploitent qu'une.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Du temps perdu.** Des heures passées sur quelque chose qui ne devrait pas en prendre, multipliées par un coût horaire.",
            "**Du revenu qui ne rentre pas.** Des affaires perdues, des relances jamais faites, des clients qui partent sans qu'on sache pourquoi.",
            "**Un risque qui coûte quand il se réalise.** Rare, cher, et souvent le plus convaincant des trois quand la personne l'a déjà vécu.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le deuxième est le plus puissant et le moins utilisé, parce qu'il demande de faire dire un chiffre à quelqu'un. C'est justement à ça que sert un appel de découverte.",
        },
        { type: ArticleBlockType.H2, text: "Où le trouver" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Pas dans une étude, pas dans un tableur. Dans ce que tes clients ont dit avant de signer, quand ils décrivaient leur situation sans savoir qu'ils te donnaient ton prix.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce que la personne dit", "Le chiffre qui est dedans"],
          rows: [
            [
              "« Je passe mes vendredis là-dessus »",
              "Un jour par semaine, soit environ vingt pour cent d'un salaire",
            ],
            [
              "« On a perdu deux gros dossiers l'an dernier »",
              "Deux fois le panier moyen, chiffre qu'elle connaît",
            ],
            ["« On relance quand on y pense »", "La part du pipeline qui s'éteint faute de suivi"],
            [
              "« J'ai recruté quelqu'un pour ça »",
              "Un salaire annuel, le repère le plus dur à contester",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La dernière ligne mérite attention. Quand quelqu'un a déjà recruté pour régler le problème, il a déjà accepté un prix, et il est bien plus élevé que le tien. C'est le repère le plus solide que tu puisses trouver.",
        },
        { type: ArticleBlockType.H2, text: "Du coût du problème au prix" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une fois le chiffre en main, le prix n'est plus une décision arbitraire, c'est un rapport. Il doit rester nettement en dessous de ce que le problème coûte sur la période où tu promets un résultat, sinon l'arbitrage devient impossible à faire pour l'acheteur.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Nettement, pas légèrement. Un prix qui représente presque tout ce que le problème coûte demande à la personne de parier sans marge. Un prix qui en représente une fraction laisse une différence visible, et c'est cette différence qui fait la décision.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Un prix construit par comparaison",
            items: [
              "Aligné sur un concurrent qui vend autre chose",
              "Justifié par le temps que tu passes",
              "Baissé dès la première hésitation",
              "Impossible à défendre autrement que par la comparaison",
            ],
          },
          good: {
            title: "Un prix construit sur le coût du problème",
            items: [
              "Rapporté à ce que la personne perd aujourd'hui",
              "Justifié par un chiffre qu'elle a elle-même donné",
              "Tenu, parce qu'il repose sur autre chose que ton ressenti",
              "Défendable en une phrase, sans citer personne d'autre",
            ],
          },
        },
        { type: ArticleBlockType.H2, text: "Présenter le prix" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le prix n'est pas un nombre qu'on lâche à la fin. C'est la dernière ligne d'un raisonnement, et le raisonnement doit précéder, sinon le chiffre arrive seul et n'est comparé à rien.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Rappelle le coût constaté** avec les mots de la personne, pas les tiens.",
            "**Annonce le prix sans le commenter.** Un prix qu'on explique trop se défend déjà.",
            "**Laisse le silence.** C'est le moment où tu apprends dans lequel des cas tu es.",
            "**Ne propose pas de remise avant qu'on te la demande.** L'offrir spontanément dit que tu n'y croyais pas.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le troisième point est le plus dur à tenir et le plus instructif. Le silence après un prix dure quelques secondes et dit beaucoup : une objection immédiate n'est pas la même chose qu'une hésitation, ni qu'une demande de version réduite.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "On repère dans tes appels ce que tes clients décrivent comme perdu : du temps, des affaires, un poste créé pour compenser. Ces éléments remontent dans ton offre, avec leurs formulations d'origine, et c'est cette matière qui rend ton prix défendable sans citer un concurrent.",
          linkLabel: "Découvrir les offres",
          linkPath: ProtectedPaths.OFFERS,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Cherche d'abord ce que le problème coûte, mensuellement, avec un chiffre venu de la personne. Place ton prix nettement en dessous sur la période où tu promets un résultat. Et présente-le comme la conclusion d'un raisonnement, pas comme une information de fin d'entretien.",
        },
      ],
      faq: [
        {
          question: "Comment fixer le prix d'une prestation ?",
          answer:
            "En partant de ce que le problème coûte à la personne, pas de ce qu'il te coûte à produire ni de ce que demandent tes concurrents. Cherche le montant perdu chaque mois, en temps, en revenu ou en risque, puis place ton prix nettement en dessous sur la période où tu promets un résultat.",
        },
        {
          question: "Faut-il s'aligner sur les prix des concurrents ?",
          answer:
            "Non. Un concurrent vend autre chose, à quelqu'un d'autre, avec des coûts que tu ne connais pas. Son prix ne dit rien de la valeur de ton offre, seulement de sa propre décision. La comparaison donne un chiffre plausible, jamais le bon.",
        },
        {
          question: "Comment justifier un prix élevé ?",
          answer:
            "Avec un chiffre que la personne a donné elle-même. « Vous m'avez dit passer un jour par semaine là-dessus » est plus solide que n'importe quel argument. Le meilleur repère est quelqu'un qui a déjà recruté pour régler le problème : il a accepté un prix bien supérieur au tien.",
        },
        {
          question: "Quand annoncer le prix pendant un rendez-vous ?",
          answer:
            "Après avoir chiffré le coût du problème, jamais avant. Un prix annoncé tôt n'est comparé à rien et paraît toujours élevé. Une fois le coût établi, le même chiffre devient un rapport, et le rapport se discute autrement.",
        },
      ],
    },
    en: {
      title: "Pricing from what the problem costs",
      excerpt:
        "Looking at competitors gives you a plausible number and never the right one. The only useful reference is what the problem costs the person living with it, and it sits in your calls.",
      description:
        "How to price an offer from the real cost of the problem, where to find that figure, and how to present a price so it is not the first subject.",
      ctaTitle: "Find your price in your calls",
      ctaText:
        "We surface what your customers say they are losing, in time and in money, before they buy. That figure is what makes your price obvious. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The most common way to set a price is to look at what others charge, then sit slightly below. It has two advantages: it is quick, and it requires speaking to nobody.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "It has one flaw that cancels both. A competitor sells something else, to someone else, on a cost base you know nothing about. Their price says nothing about what your offer is worth; it says what they decided.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A price is not deduced from what it costs you to deliver, nor from what others ask. It is deduced from what the problem costs the person living with it.",
        },
        { type: ArticleBlockType.H2, text: "The only figure that works as a reference" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Before naming a price you have to be able to answer one question: how much does this person lose every month because of the problem you solve. Until that figure exists, no price is defensible, yours or anyone else's.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That figure takes three forms, and most offers use only one.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Time lost.** Hours spent on something that should not take them, multiplied by an hourly cost.",
            "**Revenue that does not arrive.** Deals lost, follow-ups never made, customers leaving without anyone knowing why.",
            "**A risk that costs when it lands.** Rare, expensive, and often the most convincing of the three when the person has already lived it.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The second is the most powerful and the least used, because it requires making someone say a number out loud. That is exactly what a discovery call is for.",
        },
        { type: ArticleBlockType.H2, text: "Where to find it" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Not in a study, not in a spreadsheet. In what your customers said before signing, while describing their situation without knowing they were handing you your price.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["What the person says", "The figure inside it"],
          rows: [
            ["I spend my Fridays on this", "One day a week, roughly twenty percent of a salary"],
            [
              "We lost two big accounts last year",
              "Twice the average deal, a number they already know",
            ],
            [
              "We follow up when we remember to",
              "The share of pipeline that dies from no follow-up",
            ],
            ["I hired someone for this", "An annual salary, the hardest reference to argue with"],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The last row deserves attention. When someone has already hired to solve the problem, they have already accepted a price, and it is far above yours. That is the strongest reference you will find.",
        },
        { type: ArticleBlockType.H2, text: "From the cost of the problem to the price" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Once you hold that figure, the price stops being an arbitrary decision and becomes a ratio. It has to sit clearly below what the problem costs over the period in which you promise a result, otherwise the buyer cannot make the trade-off at all.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Clearly, not slightly. A price that eats nearly all of what the problem costs asks the person to bet with no margin. A price that is a fraction of it leaves a visible difference, and that difference is what makes the decision.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "A price built by comparison",
            items: [
              "Aligned on a competitor selling something else",
              "Justified by the time you spend",
              "Cut at the first hesitation",
              "Impossible to defend except by comparison",
            ],
          },
          good: {
            title: "A price built on the cost of the problem",
            items: [
              "Set against what the person loses today",
              "Justified by a number they gave you themselves",
              "Held, because it rests on something beyond your feeling",
              "Defensible in one sentence, without naming anyone else",
            ],
          },
        },
        { type: ArticleBlockType.H2, text: "Presenting the price" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A price is not a number you drop at the end. It is the last line of a reasoning, and the reasoning has to come first, otherwise the figure arrives alone and gets compared to nothing.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Restate the observed cost** in the person's words, not yours.",
            "**Name the price without commenting on it.** A price you over-explain is already being defended.",
            "**Leave the silence.** That is the moment you learn which case you are in.",
            "**Do not offer a discount before being asked.** Volunteering one says you never believed it.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The third point is the hardest to hold and the most informative. The silence after a price lasts a few seconds and says a lot: an immediate objection is not the same thing as hesitation, nor as a request for a smaller version.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "We surface from your calls what your customers describe as lost: time, deals, a role created to compensate. It comes back into your offer with its original phrasing, and that material is what makes your price defensible without citing a competitor.",
          linkLabel: "See how offers work",
          linkPath: ProtectedPaths.OFFERS,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Find what the problem costs first, monthly, with a number that came from the person. Put your price clearly below it over the period in which you promise a result. And present it as the conclusion of a reasoning, not as a piece of information at the end of a meeting.",
        },
      ],
      faq: [
        {
          question: "How do you set the price of a service?",
          answer:
            "By starting from what the problem costs the person, not from what it costs you to deliver nor from what competitors ask. Find the amount lost each month, in time, revenue or risk, then put your price clearly below it over the period in which you promise a result.",
        },
        {
          question: "Should you match competitors' prices?",
          answer:
            "No. A competitor sells something else, to someone else, on costs you do not know. Their price says nothing about the value of your offer, only about their own decision. Comparison gives a plausible number, never the right one.",
        },
        {
          question: "How do you justify a high price?",
          answer:
            "With a figure the person gave you themselves. You told me you spend a day a week on this beats any argument you could build. The strongest reference is someone who already hired to solve the problem: they accepted a price far above yours.",
        },
        {
          question: "When should you name the price in a meeting?",
          answer:
            "After quantifying the cost of the problem, never before. A price named early is compared to nothing and always sounds high. Once the cost is established, the same number becomes a ratio, and a ratio is discussed differently.",
        },
      ],
    },
  },
};
